import { createFileRoute, redirect, Link } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, Search, ChevronLeft, ChevronRight, Settings2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { adminApi, isPlatformAdmin } from '@/lib/admin'
import { StatusPill } from '@/components/rm'
import { phFormat, peso, manilaWallToIso } from '@/lib/ph'
import { BrandMark } from '@/components/brand'

export const Route = createFileRoute('/admin')({
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession()
    if (!data.session) throw redirect({ to: '/login' })
    if (!(await isPlatformAdmin())) throw redirect({ to: '/dashboard' })
  },
  component: AdminDashboard,
})

type Row = Record<string, unknown>
const money = (n: unknown) => peso(n)
const date = (v: unknown) => (v ? phFormat(new Date(v as string), 'date', { day: 'numeric', month: 'short', year: '2-digit' }) : '—')
const coName = (r: Row) => (r.company as { name?: string } | null)?.name ?? '—'

const TABS = ['overview', 'companies', 'users', 'jobs', 'quotes', 'clients'] as const
type Tab = (typeof TABS)[number]

const COLUMNS: Record<Exclude<Tab, 'overview'>, { key: string; label: string; render?: (r: Row) => ReactNode }[]> = {
  companies: [
    { key: 'name', label: 'Company' },
    { key: 'email', label: 'Email' },
    { key: 'subscription_status', label: 'Status', render: (r) => <StatusPill status={(r.subscription_status as string) || 'inactive'} /> },
    { key: 'subscription_type', label: 'Method / plan' },
    { key: 'subscription_expires_at', label: 'Expires', render: (r) => date(r.subscription_expires_at) },
    { key: 'max_users', label: 'Seats' },
    { key: 'created_at', label: 'Joined', render: (r) => date(r.created_at) },
  ],
  users: [
    { key: 'name', label: 'Name', render: (r) => `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim() || '—' },
    { key: 'email', label: 'Email' },
    { key: 'role', label: 'Role' },
    { key: 'company', label: 'Company', render: coName },
    { key: 'created_at', label: 'Joined', render: (r) => date(r.created_at) },
  ],
  jobs: [
    { key: 'name', label: 'Job' },
    { key: 'status', label: 'Status' },
    { key: 'planned_start_date', label: 'Scheduled', render: (r) => date(r.planned_start_date) },
    { key: 'company', label: 'Company', render: coName },
  ],
  quotes: [
    { key: 'title', label: 'Quote' },
    { key: 'status', label: 'Status' },
    { key: 'total', label: 'Total', render: (r) => money(r.total) },
    { key: 'company', label: 'Company', render: coName },
    { key: 'created_at', label: 'Created', render: (r) => date(r.created_at) },
  ],
  clients: [
    { key: 'name', label: 'Client', render: (r) => `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim() || (r.organization as string) || '—' },
    { key: 'organization', label: 'Business' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'company', label: 'Company', render: coName },
  ],
}

function AdminDashboard() {
  const [tab, setTab] = useState<Tab>('overview')
  return (
    <div className="min-h-screen bg-[#0f1216] text-[#e9ebf0]">
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[#12141a] px-6 py-3.5">
        <div className="flex items-center gap-3">
          <BrandMark className="h-7 w-7" />
          <span className="font-bold tracking-tight">MySerbisyo <span className="text-[#fcd116]">Admin</span></span>
        </div>
        <div className="flex items-center gap-4 text-[13px]">
          <Link to="/dashboard" className="text-token-muted hover:text-white">← Back to app</Link>
          <button onClick={() => supabase.auth.signOut().then(() => (window.location.href = '/login'))} className="text-token-muted hover:text-white">Sign out</button>
        </div>
      </header>

      <nav className="flex gap-1 overflow-x-auto border-b border-white/10 bg-[#12141a] px-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`shrink-0 whitespace-nowrap px-3.5 py-2.5 text-[13.5px] font-semibold capitalize transition ${tab === t ? 'border-b-2 border-[#fcd116] text-white' : 'text-token-muted hover:text-white'}`}>{t}</button>
        ))}
      </nav>

      <main className="p-6">
        {tab === 'overview' ? <Overview /> : <EntityTable key={tab} entity={tab} />}
      </main>
    </div>
  )
}

function Overview() {
  const { data, isLoading } = useQuery({ queryKey: ['admin-stats'], queryFn: () => adminApi<{ counts: Record<string, number>; byStatus: Record<string, number> }>({ action: 'stats' }) })
  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin text-token-muted" />
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {Object.entries(data?.counts ?? {}).map(([k, v]) => (
          <div key={k} className="rounded-[14px] border border-white/10 bg-[#161a20] p-5">
            <p className="text-[12px] font-semibold uppercase tracking-wide text-token-muted">{k}</p>
            <p className="mt-1 text-[30px] font-bold tabular-nums text-white">{v}</p>
          </div>
        ))}
      </div>
      <div className="rounded-[14px] border border-white/10 bg-[#161a20] p-5">
        <p className="mb-3 text-[13px] font-bold text-white">Subscriptions by status / method</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(data?.byStatus ?? {}).map(([k, v]) => (
            <span key={k} className="rounded-full bg-white/5 px-3 py-1 text-[12.5px] text-token-muted">{k}: <b className="text-white">{v}</b></span>
          ))}
        </div>
      </div>
    </div>
  )
}

function EntityTable({ entity }: { entity: Exclude<Tab, 'overview'> }) {
  const qc = useQueryClient()
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [editing, setEditing] = useState<Row | null>(null)
  const [inviting, setInviting] = useState<Row | null>(null)
  const [newCo, setNewCo] = useState(false)
  const cols = COLUMNS[entity]
  const { data, isLoading } = useQuery({
    queryKey: ['admin-list', entity, page, q],
    queryFn: () => adminApi<{ rows: Row[]; total: number; pageSize: number }>({ action: 'list', entity, page, search: q }),
  })
  const total = data?.total ?? 0
  const ps = data?.pageSize ?? 20
  const pages = Math.max(1, Math.ceil(total / ps))

  return (
    <div>
      <form onSubmit={(e) => { e.preventDefault(); setPage(0); setQ(search) }} className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-token-secondary" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${entity}…`} className="h-9 w-full rounded-[10px] border border-white/10 bg-[#161a20] pl-9 pr-3 text-[13.5px] text-white outline-none focus:border-[#fcd116]/50" />
        </div>
        <span className="text-[12.5px] text-token-muted">{total.toLocaleString()} total</span>
        {entity === 'companies' && (
          <button type="button" onClick={() => setNewCo(true)} className="ml-auto inline-flex items-center gap-1.5 rounded-[10px] bg-[#fcd116] px-3.5 py-2 text-[13px] font-bold text-token-primary hover:brightness-105">+ New company</button>
        )}
      </form>

      <div className="overflow-x-auto rounded-[14px] border border-white/10">
        <table className="w-full text-[13px]">
          <thead className="bg-[#161a20] text-token-muted">
            <tr>{cols.map((c) => <th key={c.key} className="px-4 py-2.5 text-left font-semibold">{c.label}</th>)}{entity === 'companies' && <th className="px-4 py-2.5" />}</tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={cols.length + 1} className="px-4 py-12 text-center text-token-muted"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
            ) : (data?.rows ?? []).length === 0 ? (
              <tr><td colSpan={cols.length + 1} className="px-4 py-12 text-center text-token-muted">Nothing found.</td></tr>
            ) : (
              (data?.rows ?? []).map((r, i) => (
                <tr key={i} className="border-t border-white/5 hover:bg-white/5">
                  {cols.map((c) => <td key={c.key} className="px-4 py-2.5 text-[#e9ebf0]">{c.render ? c.render(r) : String(r[c.key] ?? '—')}</td>)}
                  {entity === 'companies' && (
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button onClick={() => setInviting(r)} className="rounded-[8px] bg-white/10 px-2.5 py-1 text-[12px] font-semibold hover:bg-white/20">Invite user</button>
                        <button onClick={() => setEditing(r)} className="inline-flex items-center gap-1 rounded-[8px] bg-white/10 px-2.5 py-1 text-[12px] font-semibold hover:bg-white/20"><Settings2 className="h-3.5 w-3.5" /> Manage</button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-[13px] text-token-muted">
        <span>Page {page + 1} of {pages}</span>
        <div className="flex gap-2">
          <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="inline-flex items-center gap-1 rounded-[8px] border border-white/10 px-3 py-1.5 disabled:opacity-40 hover:bg-white/10"><ChevronLeft className="h-4 w-4" /> Prev</button>
          <button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} className="inline-flex items-center gap-1 rounded-[8px] border border-white/10 px-3 py-1.5 disabled:opacity-40 hover:bg-white/10">Next <ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      {editing && <SubModal company={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['admin-list', 'companies'] }) }} />}
      {inviting && <InviteModal companyId={String(inviting.id)} companyName={String(inviting.name)} onClose={() => setInviting(null)} />}
      {newCo && <NewCompanyModal onClose={() => setNewCo(false)} onCreated={() => { setNewCo(false); qc.invalidateQueries({ queryKey: ['admin-list', 'companies'] }); qc.invalidateQueries({ queryKey: ['admin-stats'] }) }} />}
    </div>
  )
}

function ModalShell({ title, sub, children }: { title: string; sub?: string; children: ReactNode }) {
  return <div className="space-y-3">
    <p className="text-[15px] font-bold text-white">{title}</p>
    {sub && <p className="-mt-2 text-[12.5px] text-token-muted">{sub}</p>}
    {children}
  </div>
}
const mfield = 'h-9 w-full rounded-[10px] border border-white/10 bg-[#0f1216] px-3 text-[13.5px] text-white outline-none focus:border-[#fcd116]/50'
const mwrap = 'fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4'
const mcard = 'w-full max-w-md rounded-[16px] border border-white/10 bg-[#161a20] p-6'

function InviteModal({ companyId, companyName, onClose }: { companyId: string; companyName: string; onClose: () => void }) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('user')
  const [busy, setBusy] = useState(false)
  const [link, setLink] = useState<string | null>(null)
  const send = async () => {
    setBusy(true)
    try {
      const { data, error } = await supabase.functions.invoke('invite-create', { body: { companyId, email, role } })
      if (error || !(data as { ok?: boolean })?.ok) throw new Error('Could not send invite')
      setLink((data as { link: string }).link)
      toast.success((data as { emailed: boolean }).emailed ? 'Invite emailed' : 'Invite created (email pending)')
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') } finally { setBusy(false) }
  }
  return <div className={mwrap} onClick={onClose}><div className={mcard} onClick={(e) => e.stopPropagation()}>
    <ModalShell title={`Invite to ${companyName}`} sub="They'll get an email to set a password and join this company.">
      <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@email.com" className={mfield} />
      <select value={role} onChange={(e) => setRole(e.target.value)} className={mfield}>
        <option value="user">Team member</option>
        <option value="company-admin">Admin</option>
      </select>
      {link && <p className="break-all rounded-[8px] bg-white/5 p-2 text-[11.5px] text-token-muted">Invite link: {link}</p>}
    </ModalShell>
    <div className="mt-5 flex justify-end gap-2">
      <button onClick={onClose} className="rounded-[10px] border border-white/10 px-4 py-2 text-[13.5px] font-semibold text-token-muted hover:bg-white/10">Close</button>
      <button onClick={send} disabled={busy || !email} className="inline-flex items-center gap-2 rounded-[10px] bg-[#fcd116] px-4 py-2 text-[13.5px] font-bold text-token-primary disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Send invite</button>
    </div>
  </div></div>
}

function NewCompanyModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [seats, setSeats] = useState(3)
  const [trialDays, setTrialDays] = useState(14)
  const [busy, setBusy] = useState(false)
  const [link, setLink] = useState<string | null>(null)
  const create = async () => {
    setBusy(true)
    try {
      const { data, error } = await supabase.functions.invoke('invite-create', { body: { email, newCompany: { name, seats, trialDays } } })
      if (error || !(data as { ok?: boolean })?.ok) throw new Error('Could not create company')
      setLink((data as { link: string }).link)
      toast.success((data as { emailed: boolean }).emailed ? `Trial company created — owner invited` : 'Company created (email pending)')
      onCreated()
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') } finally { setBusy(false) }
  }
  return <div className={mwrap} onClick={onClose}><div className={mcard} onClick={(e) => e.stopPropagation()}>
    <ModalShell title="New trial company" sub="Creates a company on a free trial and emails the owner an invite to set up.">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Business name" className={mfield} />
      <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Owner email" className={mfield} />
      <div className="flex gap-3">
        <label className="flex-1"><span className="mb-1 block text-[12px] text-token-muted">Seats</span><input type="number" min={1} value={seats} onChange={(e) => setSeats(e.target.valueAsNumber || 1)} className={mfield} /></label>
        <label className="flex-1"><span className="mb-1 block text-[12px] text-token-muted">Trial days</span><input type="number" min={1} value={trialDays} onChange={(e) => setTrialDays(e.target.valueAsNumber || 14)} className={mfield} /></label>
      </div>
      {link && <p className="break-all rounded-[8px] bg-white/5 p-2 text-[11.5px] text-token-muted">Invite link: {link}</p>}
    </ModalShell>
    <div className="mt-5 flex justify-end gap-2">
      <button onClick={onClose} className="rounded-[10px] border border-white/10 px-4 py-2 text-[13.5px] font-semibold text-token-muted hover:bg-white/10">Close</button>
      <button onClick={create} disabled={busy || !name || !email} className="inline-flex items-center gap-2 rounded-[10px] bg-[#fcd116] px-4 py-2 text-[13.5px] font-bold text-token-primary disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Create & invite</button>
    </div>
  </div></div>
}

function SubModal({ company, onClose, onSaved }: { company: Row; onClose: () => void; onSaved: () => void }) {
  const [status, setStatus] = useState((company.subscription_status as string) || 'active')
  const [type, setType] = useState((company.subscription_type as string) || 'bank')
  const [expires, setExpires] = useState((company.subscription_expires_at as string | null)?.slice(0, 10) ?? '')
  const [busy, setBusy] = useState(false)
  const save = async () => {
    setBusy(true)
    try {
      await adminApi({ action: 'set_subscription', companyId: company.id, status, type, expires_at: expires ? manilaWallToIso(expires, '00:00:00')! : null })
      toast.success('Subscription updated'); onSaved()
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') } finally { setBusy(false) }
  }
  const field = 'h-9 w-full rounded-[10px] border border-white/10 bg-[#0f1216] px-3 text-[13.5px] text-white outline-none focus:border-[#fcd116]/50'
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-[16px] border border-white/10 bg-[#161a20] p-6" onClick={(e) => e.stopPropagation()}>
        <p className="text-[15px] font-bold text-white">{String(company.name)}</p>
        <p className="mb-4 text-[12.5px] text-token-muted">Set how this company pays and its access.</p>
        <div className="space-y-3">
          <div><label className="mb-1 block text-[12px] font-semibold text-token-muted">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={field}>
              <option value="active">active (has access)</option>
              <option value="trialing">trialing</option>
              <option value="past_due">past_due</option>
              <option value="inactive">inactive (blocked)</option>
              <option value="canceled">canceled (blocked)</option>
            </select></div>
          <div><label className="mb-1 block text-[12px] font-semibold text-token-muted">Payment method / plan</label>
            <select value={type} onChange={(e) => setType(e.target.value)} className={field}>
              <option value="bank">Direct deposit (bank)</option>
              <option value="monthly">Stripe · Monthly</option>
              <option value="annual">Stripe · Annual</option>
              <option value="trial">Trial</option>
              <option value="manual-tua">Manual (legacy)</option>
            </select></div>
          <div><label className="mb-1 block text-[12px] font-semibold text-token-muted">Access until (optional — blank = no expiry)</label>
            <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className={field} /></div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-[10px] border border-white/10 px-4 py-2 text-[13.5px] font-semibold text-token-muted hover:bg-white/10">Cancel</button>
          <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 rounded-[10px] bg-[#fcd116] px-4 py-2 text-[13.5px] font-bold text-token-primary hover:brightness-105 disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
        </div>
      </div>
    </div>
  )
}
